// Client-side "have I looked at this widget lately?" store backing the widget
// announcement chips in the feed. A chip renders loud (tinted) while its
// announcement is newer than the last time the viewer actually saw the source
// widget, then decays to the quiet gray treatment. "Saw" means: jumped to it
// from a chip or context menu, or scrolled it into view on the widgets pane.
//
// State is per-browser (localStorage) — this is presence-of-attention, not a
// synced read receipt, so cross-device fidelity is intentionally out of scope.

export const WIDGET_VIEWED_CHANGE_EVENT = 'output:internal:widgets:viewed-at-change';

const STORAGE_KEY = 'echo:widget-viewed-at:v1';
const MAX_ENTRIES = 300;
// Repeated intersection callbacks while a widget sits on screen shouldn't
// thrash localStorage; skip writes that would only nudge a fresh timestamp.
const MIN_REWRITE_INTERVAL_SECONDS = 15;

// Parsed-map cache so per-render lookups (every chip's useSyncExternalStore
// snapshot) don't re-read and re-parse the whole localStorage blob. Invalidated
// on writes here and on cross-tab `storage` events.
let cachedEntries = null;

const readAll = () => {
  if (cachedEntries) return cachedEntries;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    cachedEntries = parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    cachedEntries = {};
  }

  return cachedEntries;
};

const writeAll = (entries) => {
  cachedEntries = entries;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or unavailable — chips just stay loud, which is safe.
  }
};

const notifyChange = () => {
  window.dispatchEvent(new CustomEvent(WIDGET_VIEWED_CHANGE_EVENT));
};

export function getWidgetViewedAt(widgetId) {
  if (!widgetId) return null;
  const value = readAll()[String(widgetId)];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function markWidgetViewed(widgetId, at = Math.floor(Date.now() / 1000)) {
  if (!widgetId) return;

  const entries = readAll();
  const key = String(widgetId);
  const existing = entries[key];
  if (typeof existing === 'number' && at - existing < MIN_REWRITE_INTERVAL_SECONDS) return;

  entries[key] = at;

  const keys = Object.keys(entries);
  if (keys.length > MAX_ENTRIES) {
    keys
      .sort((a, b) => (entries[a] || 0) - (entries[b] || 0))
      .slice(0, keys.length - MAX_ENTRIES)
      .forEach((staleKey) => delete entries[staleKey]);
  }

  writeAll(entries);
  notifyChange();
}

export function clearWidgetViewedStore() {
  cachedEntries = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  notifyChange();
}

export function subscribeWidgetViewed(callback) {
  const handleStorage = (event) => {
    if (event.key && event.key !== STORAGE_KEY) return;
    // Another tab wrote — drop the cache so the next snapshot re-reads.
    cachedEntries = null;
    callback();
  };

  window.addEventListener(WIDGET_VIEWED_CHANGE_EVENT, callback);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener(WIDGET_VIEWED_CHANGE_EVENT, callback);
    window.removeEventListener('storage', handleStorage);
  };
}
